/* eslint-disable @typescript-eslint/no-empty-function */
import {
    DialogPositioningBehavior,
    DialogProps,
    DialogType,
    DialogWidget,
    HorizontalOriginOption,
    TextFieldWidget,
    VerticalOriginOption,
} from '@tableau/widgets';
import * as React from 'react';

import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { Keys } from '../../commonInfra/utils/Keys';
import { Messages } from '../../../gen-src/Localize';
import { isMetricsEnabled } from '../../metrics/util/MetricUtils';

import { KeydownOutsideDetector } from '../common/KeydownOutsideDetector';
import { MousedownOutsideDetector } from '../common/MousedownOutsideDetector';
import { setupUIBindingForReactFunctionalComponent } from '../common/UIBindingForReact';
import { OmniboxV2ViewModel } from '../viewModels/OmniboxV2ViewModel';
import { ContentSuggestion } from './ContentSuggestion';
import { QuerySuggestion } from './QuerySuggestion';
import { QuickLinks } from './QuickLinks';
import { popupOverallStyles } from '../../OmniboxComponentStyles';
import { EmptyStateContentSuggestion } from './EmptyStateContentSuggestion';
import { OmniboxConfiguration } from '../models/Types';
import { OmniboxConfigurationService } from '../OmniboxConfigurationService';

export type OmniboxProps = {
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed by the toggle button, the focus should remain in the toggle button.
     * Or if the omnibox is closed by pressing ESC in it, the focus should remain in the toggle button.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithFocus: () => void;
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed because the whole JavaScript window loses focus e.g. the user is in a Viz viewer page and searches something to pop up the
     * search result, then the user clicks the viz which is inside an iFrame.
     * In this case, the whole omnibox should be close and the toggle button should not hold the focus.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithoutFocus: () => void;
    shouldOpenWithFocusInSearchTextbox: boolean;
    isSearchOpen?: boolean;
    viewModel?: OmniboxV2ViewModel;
    isSearchInputBoxActive: (isActive: boolean) => void;
    inSearchResultPage?: boolean;
    searchTerm?: string;
    onSearchTermChanged?: (searchTerm: string) => void;
    onFocus?: () => void;
    omniboxConfiguration: OmniboxConfiguration;
    onViewModelReady?: (viewModel: OmniboxV2ViewModel) => void;
}

/* Note: the view model of Omnibox is directly instantiated below as a singleton. It's not a normal way to do it in MVVM. It won't
work if there's multiple Omniboxes in the UI.
The reason it's done in this way here is that the parent component of the Omnibox is not a MVVM implementation and currently there's
only one Omnibox in the UI.
A suggested way to instantiate a view model should be:
1. If the parent of the view model is also an MVVM implemenation, the parent view model should instantiate the current view model.
2. If the parent of the view model is not an MVVM implementation, an HOC of current component should be created. The HOC component should
create the instance of the view model and
work as an intermediate layer between the parent component and the current component. Here is an example based on Omnibox.

// SomeParent is the parent which renders Omnibox. It talks to OmniboxMVVM component instead of directly talking to Omnibox view component.
export function SomeParent(props: SomeParentProps): JSX.Element {
    return <OmniboxMVVM />;
}

// OmniboxMVVM is the HOC of Omnibox MVVM implementation. It's responsibility is to create the view model instance for
each of MVVM instance.
export function OmniboxMVVM(props: OmniboxProps): JSX.Element {
    let viewModel=new OmniboxViewModel();
    return <Omnibox {...props, viewModel=viewModel}/>;
}

// Omnibox is the actual view component of the MVVM implementation.
export function Omnibox(props: OmniboxProps): JSX.Element {
    let viewModel=props.viewModel;
    return <div>{viewModel.whatEverData}</div>;
} */
const viewModelSingleton = new OmniboxV2ViewModel();

const localizationSentinel = '@!@!@!@!@!@!Omni@!@!@!@!@!@!';
export const SearchResultElementId = 'OmniboxSearchResult';
export const SearchResultItemIdPrefix = 'OmniboxSearchResult-';

export function OmniboxV2(props: OmniboxProps): JSX.Element {
    const TextBoxName = 'omniboxTextBox';
    const textBoxElementRef = React.useRef<any>(null);
    const currentHighlightedElementRef = React.useRef<any>(null);

    let viewModel: OmniboxV2ViewModel;

    if (props.viewModel) {
        // TBD: after the old code is removed, the following type casting should also be removed.
        viewModel = props.viewModel as OmniboxV2ViewModel;
    } else {
        viewModel = viewModelSingleton;
    }

    if (props.onViewModelReady) {
        props.onViewModelReady(viewModel);
    }

    viewModel.askingForCloseWithFocus = props.closedWithFocus;
    viewModel.askingForCloseWithoutFocus = props.closedWithoutFocus;
    viewModel.focusOnSearchBox = () => {
        textBoxElementRef?.current?.querySelector('input').focus();
    };
    viewModel.onSearchTermChanged = props.onSearchTermChanged;

    setupUIBindingForReactFunctionalComponent(viewModel);

    const { shouldOpenWithFocusInSearchTextbox, isSearchInputBoxActive, searchTerm, inSearchResultPage } = props;

    viewModel.inSearchResultPage = inSearchResultPage;

    OmniboxConfigurationService.instance.initialize(props.omniboxConfiguration);

    React.useEffect(
        () => {
            viewModel.initialize(shouldOpenWithFocusInSearchTextbox);

            return () => {
                viewModel.onUnmount();
            };
        },
        [shouldOpenWithFocusInSearchTextbox, viewModel]
    );

    React.useEffect(
        () => {
            viewModel.searchTerm = searchTerm || '';
        }, [searchTerm, viewModel]
    );

    const resultElementRef = React.useRef<any>(null);
    const pressEnterSeeAllElementRef = React.useRef<any>(null);

    let detachMousedownOutsideDetector: () => void;
    let detachKeydownOutsideDetector: () => void;

    React.useEffect(
        () => {
            isSearchInputBoxActive(viewModel.isInputboxActive);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isSearchInputBoxActive, viewModel.isInputboxActive]
    );
    React.useEffect(() => {
        if (viewModel.searchResultUIVisible) {
            const timeoutForSearchResultUIUseEffectHook = setTimeout(() => {
                // This will close the search panel when clicking on an iframe
                // (NLP tab and the viz) because window will lose focus when clicking into the iframe
                window.addEventListener('blur', onWindowBlur);

                // This noncompliance existed before the lint rule was enabled.
                // eslint-disable-next-line react-hooks/exhaustive-deps
                detachMousedownOutsideDetector = MousedownOutsideDetector.attach(
                    resultElementRef.current,
                    (eventTarget: Element) => {
                        if (!(textBoxElementRef.current && textBoxElementRef.current.contains(eventTarget)) && !viewModel.querySuggestionViewModel.clearConfirmationDialogIsOpen) {
                            viewModel.closeSearch(true);
                        }
                    });

                // This noncompliance existed before the lint rule was enabled.
                // eslint-disable-next-line react-hooks/exhaustive-deps
                detachKeydownOutsideDetector = KeydownOutsideDetector.attach(
                    resultElementRef.current,
                    (eventTarget: Element) => {
                        if (!(textBoxElementRef.current && textBoxElementRef.current.contains(eventTarget)) && !viewModel.querySuggestionViewModel.clearConfirmationDialogIsOpen) {
                            viewModel.closeSearch(true);
                        }
                    }, ['Enter', 'Space', 'Escape']);
            });

            return () => {
                window.removeEventListener('blur', onWindowBlur);
                if (timeoutForSearchResultUIUseEffectHook) {
                    clearTimeout(timeoutForSearchResultUIUseEffectHook);
                }

                if (detachMousedownOutsideDetector) {
                    detachMousedownOutsideDetector();
                }
                if (detachKeydownOutsideDetector) {
                    detachKeydownOutsideDetector();
                }
            };
        }
    }, [viewModel.searchResultUIVisible]);

    const dialogWidgetContent: JSX.Element = <>
        <div className='tb-omnibox-status' aria-live='polite' role='status' style={{ display: 'none' }}>
            {/* {viewModel.statusForAccessibility} */}
            {renderPressEnterToSeeAllContent()}
        </div>
        {/*The following <div> requires the 'tb-omnibox-popup' class name.  VizPortalClient's VizViewerPageHeader  
        evaluates eventTargetOutOfSearch by checking if a click is within a 'tb-omnibox-popup' element*/}
        <div id={SearchResultElementId} className={[popupOverallStyles, 'tb-omnibox-popup'].join(' ')} ref={resultElementRef} role='listbox' aria-label={Messages.omniboxSearchResults()}>
            <QuerySuggestion viewModel={viewModel.querySuggestionViewModel} />
            {!viewModel.searchTerm ?
                <>
                    <QuickLinks viewModel={viewModel.quickLinksViewModel} />
                    {FeatureFlagService.instance.getValue(FeatureFlag.smarterSearchEnabled) ?
                        <EmptyStateContentSuggestion viewModel={viewModel.emptyStateContentSuggestionViewModel} /> : null}
                </>
                : <ContentSuggestion viewModel={viewModel.contentSuggestionViewModel} />}
        </div>
    </>;

    const dialogWidgetProps: DialogProps = {
        title: Messages.omniboxSearchResults(),
        dialogBodyStyle: { margin: 0, padding: 0 },
        // minWidth need to be same as tb-omnibox-search-box-long width in omniboxReact.styl
        dialogStyle: { marginTop: '4px', minWidth: '560px' },
        type: DialogType.Uber,
        isModal: false,
        isGlassTransparent: true,
        shouldReturnFocusOnClose: false,
        handleGlassClick: () => { },
        onCloseRequest: () => { },
        testId: 'omnibox-v2-popup',
        showCloseButton: false,
        showFooter: false,
        disableAutoFocus: true,
        positioningBehavior: DialogPositioningBehavior.Anchored,
        getAnchorClientRect: () => getTextBoxRect(),
        anchorOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom },
        children: dialogWidgetContent,
    };

    function setInputboxRef(element: HTMLElement | null): void {
        textBoxElementRef.current = element;
        currentHighlightedElementRef.current = viewModel.currentKeyboardNavigableItem === viewModel.searchBoxNavigationItem ? element : null;
    }

    const paddingRightForInputText = 20;
    const pressEnterToSeeAllWidth = pressEnterSeeAllElementRef.current ? pressEnterSeeAllElementRef.current.clientWidth + paddingRightForInputText + 'px' : '0px';

    const searchBoxAccessibilityProps: React.AriaAttributes = {
        'aria-haspopup': 'listbox',
        'aria-expanded': viewModel.searchResultUIVisible,
        'aria-autocomplete': 'list',
        'aria-activedescendant': viewModel.activeKeyboardNavigableItem?.getIdentifier()
    };
    if (viewModel.searchResultUIVisible) {
        searchBoxAccessibilityProps['aria-controls'] = SearchResultElementId;
    }

    const metricsEnabled = isMetricsEnabled();

    /* eslint-disable jsx-a11y/no-autofocus */
    return (<span className={['tb-omnibox', (inSearchResultPage ? 'for-serp' : '')].join(' ')}>
        <div className='tb-omnibox-text-box'>
            <div ref={setInputboxRef} className={['tb-omnibox-search-box', (viewModel.isInputboxActive ? 'tb-omnibox-search-box-long' : null), (viewModel.activeKeyboardNavigableItem !== viewModel.searchBoxNavigationItem ? 'not-active-keyboard-navigation-item' : null)].join(' ')}
                onTransitionEnd={FeatureFlagService.instance.getValue(FeatureFlag.searchQuickLinks) ? e => viewModel.onSearchBoxTransitionEnd() : () => { }}>
                <TextFieldWidget
                    kind='search'
                    label={null}
                    name={TextBoxName}
                    testId='omnibox'
                    autoComplete={'off'}
                    placeholder={OmniboxConfigurationService.instance.configuration.searchPlaceHolderText || (metricsEnabled ? Messages.omniboxInputboxPlaceHolder() : Messages.omniboxInputboxPlaceHolderNoMetrics())}
                    style={{ paddingRight: pressEnterToSeeAllWidth }}
                    onClick={() => viewModel.onSearchBoxClicked()}
                    onFocus={() => { viewModel.onSearchBoxFocus(); if (props.onFocus) props.onFocus(); }}
                    onBlur={() => viewModel.onSearchBoxBlur()}
                    autoFocus={props.shouldOpenWithFocusInSearchTextbox}
                    value={viewModel.searchTerm}
                    onKeyDown={e => handleSearchBoxKeyPress(e)}
                    onChange={e => viewModel.onSearchTextChange(e.target.value)}
                    role='combobox'
                    id='search-omnibox'
                    {...searchBoxAccessibilityProps}
                ></TextFieldWidget>
                {pressEnterToSeeAllSection()}
            </div>
            {viewModel.searchResultUIVisible ?
                <DialogWidget {...dialogWidgetProps}>
                </DialogWidget> : null}
        </div>
    </span >);

    function handleSearchBoxKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.keyCode) {
            case Keys.Escape:
                viewModel.onEscapePressed();
                break;
            case Keys.Enter:
                viewModel.onEnterPressed();
                break;
            case Keys.ArrowLeft:
                viewModel.onArrowLeftPressed(() => event.preventDefault());
                break;
            case Keys.ArrowRight:
                viewModel.onArrowRightPressed(() => event.preventDefault());
                break;
            case Keys.ArrowDown:
                viewModel.onArrowDownPressed(() => event.preventDefault());
                break;
            case Keys.ArrowUp:
                viewModel.onArrowUpPressed(() => event.preventDefault());
                break;
            case Keys.Tab:
                viewModel.onTabKeyPressed(() => event.preventDefault());
        }
    }

    function pressEnterToSeeAllSection(): JSX.Element | null {
        return !viewModel.searchTerm ? null :
            <div className='tb-omnibox-press-enter-to-see-all' ref={pressEnterSeeAllElementRef}>
                {renderPressEnterToSeeAllContent()}
            </div>;
    }

    function renderPressEnterToSeeAllContent() {
        const messageWithSentinel = Messages.omniboxPressToSeeAll({ enter: localizationSentinel });
        const messagePieces = messageWithSentinel.split(localizationSentinel);

        return <>
            <span>{messagePieces[0]}</span>
            <strong>{Messages.omniboxPressToSeeAllEnterPart()}</strong>
            <span>{messagePieces[1]}</span>
        </>;
    }

    function getTextBoxRect(): ClientRect {
        if (textBoxElementRef?.current) {
            return textBoxElementRef.current.getBoundingClientRect();
        }

        return {} as ClientRect;
    }

    function onWindowBlur() {
        window.removeEventListener('blur', onWindowBlur);
        viewModel.onWindowBlurred();
    }
}